import { createSelector } from 'reselect';
import { v4 } from 'uuid';
import { produce } from 'immer';
import { difference, pullAllBy } from 'lodash-es';
import { ScopedType, ScopedAsyncType } from './scoped-type';
import * as service from '../../services/soil-sample-order-service';
import { asyncAction } from './async-action';
import { actions as orderActions } from './soil-sample-order-dux';

const Type = ScopedType('SOIL_SAMPLING');
const AsyncType = ScopedAsyncType('SOIL_SAMPLING');
const SLICE = 'soilSampling';
const NULL_MAP = {};

const ACCEPT_ORDER = AsyncType('ACCEPT_ORDER');
const CLEAR_FAILURE = Type('CLEAR_FAILURE');
const LOAD_COMPLETED_ORDERS = AsyncType('LOAD_COMPLETED_ORDERS');
const LOAD_ORDERS = AsyncType('LOAD_ORDERS');
const LOAD_STATS = AsyncType('LOAD_STATS');
const REJECT_ORDER = AsyncType('REJECT');
const RELOAD_ORDERS = AsyncType('RELOAD_ORDERS');
const SET_FILTERED_ORDERS = Type('SET_FILTERED_ORDERS');
const SET_MAP = Type('SET_MAP');
const SET_SCHEDULED_FILTER = Type('SET_SCHEDULED_FILTER');
const SET_SELECTED_FIELD_SPECIALIST = Type('SET_SELECTED_FIELD_SPECIALIST');
const SET_SELECTED_ORDERS = Type('SET_SELECTED_ORDERS');
const SET_FOCUSED_ROW_ID = Type('SET_FOCUSED_ROW_ID');
const CLEAR_FOCUSED_ROW = Type('CLEAR_FOCUSED_ROW');
const TOGGLE_ORDER_SELECTION = Type('TOGGLE_ORDER_SELECTION');

/* prettier-ignore */
export const selectors = {
  completedOrders: (state) => state[SLICE].completedOrders,
  fieldSpecialists: createSelectFieldSpecialists(),
  filteredOrderIds: (state) => state[SLICE].filteredOrderIds,
  findOrder: (state) => (aaid) => state[SLICE].orders?.find(aaidMatcher.bind(null, aaid)),
  isLoadingCompletedOrders: (state) => state[SLICE].isLoadingCompletedOrders,
  isLoadingOrders: (state) => Boolean(state[SLICE].requestId),
  isWorking: (state) => state[SLICE].working,
  map: (state) => state[SLICE].map || NULL_MAP,
  orders: (state) => state[SLICE].orders,
  scheduledFilter: (state) => state[SLICE].scheduledFilter,
  selectedFieldSpecialist: (state) => state[SLICE].selectedFieldSpecialist || null,
  selectedOrderIds: (state) => state[SLICE].selectedOrders,
  focusedRowId: (state) => state[SLICE].focusedRowId,
  clearFocusedRow: (state) => state[SLICE].clearFocusedRow,
  selectedAaids: createSelectSelectedAaids(),
  stats: (state) => state[SLICE].stats,
  tab: (state) => state[SLICE].tab,
}

export const actions = {
  acceptOrder,
  loadCompletedOrders,
  loadOrders,
  loadStats,
  rejectOrder,
  reloadOrder,
  setFilteredOrders,
  setMap,
  setOrdersSelected,
  setFocusedRowId,
  clearFocusedRow,
  setScheduledFilter,
  setSelectedFieldSpecialist,
  toggleOrderSelection,
  unloadCompletedOrders,
};

const reducerHandlers = {
  [ACCEPT_ORDER.complete]: reduceAcceptOrderComplete,
  [ACCEPT_ORDER.failure]: reduceAcceptOrderFailure,
  [ACCEPT_ORDER.start]: reduceAcceptOrderStart,
  [CLEAR_FAILURE]: reduceClearFailure,
  [LOAD_COMPLETED_ORDERS.complete]: reduceLoadCompletedOrdersComplete,
  [LOAD_COMPLETED_ORDERS.start]: reduceLoadCompletedOrdersStart,
  [LOAD_ORDERS.complete]: reduceLoadOrdersComplete,
  [LOAD_ORDERS.start]: reduceLoadOrdersStart,
  [LOAD_STATS.complete]: reduceLoadStatsComplete,
  [REJECT_ORDER.complete]: reduceRejectOrderComplete,
  [REJECT_ORDER.failure]: reduceRejectOrderFailure,
  [REJECT_ORDER.start]: reduceRejectOrderStart,
  [RELOAD_ORDERS.complete]: reduceReloadOrdersComplete,
  [SET_FILTERED_ORDERS]: reduceSetFilteredOrders,
  [SET_MAP]: reduceSetMap,
  [SET_SCHEDULED_FILTER]: reduceSetScheduledFilter,
  [SET_SELECTED_FIELD_SPECIALIST]: reduceSetSelectedFieldSpecialist,
  [SET_SELECTED_ORDERS]: reduceSetSelectedOrders,
  [SET_FOCUSED_ROW_ID]: reduceSetFocusedRowId,
  [CLEAR_FOCUSED_ROW]: reduceClearFocusedRow,
  [TOGGLE_ORDER_SELECTION]: reduceToggleOrderSelection,
};

export const reducers = {
  [SLICE]: (state = {}, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};

function createSelectFieldSpecialists() {
  return createSelector(
    (state) => state.soilSampleOrder.agronomists || [],
    (agronomistUsers) =>
      agronomistUsers.filter(({ role }) => role === 'fieldSpecialist'),
  );
}

function createSelectSelectedAaids() {
  return createSelector(
    (state) => state[SLICE].selectedOrders,
    (state) => state[SLICE].orders,
    (selectedOrders, orders) => {
      if (!selectedOrders) return [];
      return selectedOrders.map((id) =>
        orders.find((o) => o.id === id)?.aaid?.toUpperCase(),
      );
    },
  );
}

function aaidMatcher(aaid, order) {
  return order.aaid.toUpperCase() === aaid.toUpperCase();
}

function setMap(map) {
  return { type: SET_MAP, payload: map };
}

function setOrdersSelected(selectedRowIds) {
  return { type: SET_SELECTED_ORDERS, payload: selectedRowIds };
}

function setFocusedRowId(focusedRowId) {
  return { type: SET_FOCUSED_ROW_ID, payload: focusedRowId };
}

function clearFocusedRow() {
  return { type: CLEAR_FOCUSED_ROW };
}

function toggleOrderSelection(aaid) {
  return { type: TOGGLE_ORDER_SELECTION, payload: aaid };
}

function loadStats() {
  return asyncAction(LOAD_STATS, () => service.getDashboardStats());
}

function acceptOrder(data) {
  const transformError = (error) => {
    const statusCode = error.isAxiosError && error.response?.status;

    if (statusCode === 409) {
      throw new Error('This order has already been accepted by another user.');
    }

    throw new Error(
      'Error accepting order. Please try again or contact support if the problem persists.',
    );
  };

  return asyncAction(ACCEPT_ORDER, (dispatch) =>
    service
      .acceptOrder(data)
      .catch(transformError)
      .then(() => dispatch(orderActions.cancelOrderEdit())),
  );
}

function rejectOrder(order) {
  return asyncAction(REJECT_ORDER, (dispatch) =>
    service
      .rejectOrder(order)
      .then(() => dispatch(orderActions.cancelOrderEdit())),
  );
}

function reloadOrder(ids, filters) {
  return asyncAction(
    RELOAD_ORDERS,
    () => service.getInternalOrder(ids, filters),
    { completePayload: { ids } },
  );
}

function loadOrders(filters) {
  const requestId = v4();

  return asyncAction(
    LOAD_ORDERS,
    async (_, getState) => {
      const selected = getState()[SLICE].selectedOrders;
      const orders = await service.getInternalOrders(filters, selected);

      if (getState()[SLICE].requestId === requestId) return orders;

      return null;
    },
    { startPayload: { requestId } },
  );
}

function loadCompletedOrders(filters) {
  return asyncAction(LOAD_COMPLETED_ORDERS, async () =>
    service.getCompletedInternalOrders(filters),
  );
}

function unloadCompletedOrders() {
  return asyncAction(LOAD_COMPLETED_ORDERS, async () => null);
}

function setSelectedFieldSpecialist(fieldSpecialist) {
  return { type: SET_SELECTED_FIELD_SPECIALIST, payload: fieldSpecialist };
}

function setFilteredOrders(orderIds) {
  return { type: SET_FILTERED_ORDERS, payload: orderIds };
}

function setScheduledFilter(date) {
  return { type: SET_SCHEDULED_FILTER, payload: date };
}

function reduceClearFailure(state) {
  delete state.error;
}

function reduceSetMap(state, payload) {
  state.map = payload;
}

function reduceReloadOrdersComplete(state, { payload, ids }) {
  payload.forEach((newOrder) => {
    const existingOrder = state.orders.find((o) => o.id === newOrder.id);

    if (existingOrder) {
      Object.assign(existingOrder, newOrder);
    } else {
      state.orders.push(newOrder);
    }
  });

  const returnedIds = payload.map((o) => o.id);
  const gone = difference(ids, returnedIds);
  const toRemove = gone.map((id) => ({ id }));
  pullAllBy(state.orders, toRemove, 'id');
}

function reduceLoadCompletedOrdersStart(state) {
  state.isLoadingCompletedOrders = true;
  delete state.completedOrders;
}

function reduceLoadCompletedOrdersComplete(state, payload) {
  delete state.isLoadingCompletedOrders;
  state.completedOrders = payload;
}

function reduceLoadStatsComplete(state, payload) {
  state.stats = payload;
}

function reduceLoadOrdersStart(state, payload = {}) {
  Object.assign(state, payload);
}

function reduceLoadOrdersComplete(state, payload) {
  if (payload) {
    state.orders = payload;
    delete state.requestId;
  }
}

function reduceSetSelectedOrders(state, selectedIds) {
  delete state.focusedRowId;
  delete state.clearFocusedRow;
  state.selectedOrders = selectedIds;
}

function reduceSetFocusedRowId(state, focusedRowId) {
  delete state.clearFocusedRow;
  state.focusedRowId = focusedRowId;
}

function reduceClearFocusedRow(state) {
  delete state.focusedRowId;
  state.clearFocusedRow = true;
}

function reduceAcceptOrderStart(state) {
  delete state.error;
  state.working = true;
}

function reduceAcceptOrderComplete(state) {
  state.working = false;
}

function reduceAcceptOrderFailure(state, error) {
  state.working = false;
  state.error = error.message || error;
}

function reduceRejectOrderStart(state) {
  delete state.error;
  state.working = true;
}

function reduceRejectOrderComplete(state) {
  state.working = false;
}

function reduceRejectOrderFailure(state, error) {
  state.working = false;
  state.editOrder.error = error.message || error;
}

function reduceSetSelectedFieldSpecialist(state, user) {
  state.selectedFieldSpecialist = user;
}

function reduceSetScheduledFilter(state, date) {
  state.scheduledFilter = date;
}

function reduceToggleOrderSelection(state, aaid) {
  state.selectedOrders = state.selectedOrders || [];
  const id = state.orders.find(aaidMatcher.bind(null, aaid))?.id;

  if (state.selectedOrders.includes(id)) {
    state.selectedOrders = state.selectedOrders.filter((o) => o !== id);
  } else if (id) {
    state.selectedOrders.push(id);
  }
}

function reduceSetFilteredOrders(state, filteredIds) {
  state.filteredOrderIds = filteredIds;
}
